/**
 * Published UI strings + property CMS snapshots from `generated/*.json` (Phase E).
 * Regenerate with `npm run i18n:export` or `npm run i18n:export -- --bootstrap-from-locales`.
 *
 * `translations` in JSON use plain `@` (e.g. email placeholders). vue-i18n requires escaped
 * storage at runtime — we apply that when building the in-memory message trees.
 */
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from '../../../common/localeConstants.js';
import { escapeVueI18nAtInTranslationsTree } from '../../../common/vueI18nMessageEscaping.js';
import { mergeFloorsWithRoomLocaleFallback } from '../../../common/propertyRoomLocaleFallback.js';

const modules = import.meta.glob('./generated/*.json', { eager: true });

function load() {
  const byLocale = {};
  for (const path of Object.keys(modules)) {
    const m = path.match(/\.\/generated\/([a-z]{2})\.json$/);
    if (!m) continue;
    const data = modules[path]?.default ?? modules[path];
    byLocale[m[1]] = data;
  }

  const publishedLocaleMessages = {};
  const publishedPropertyContentByLocale = {};
  /** SHA-256 from `generated/*.json` `version.translationsFingerprint` when exported via API (matches GET /v1/i18n/version). */
  const publishedTranslationFingerprints = {};
  /** SHA-256 from `version.propertyFingerprint` when exported via API (matches GET /v1/property/version). */
  const publishedPropertyFingerprints = {};

  for (const loc of SUPPORTED_LOCALES) {
    const bundle = byLocale[loc];
    publishedLocaleMessages[loc] = escapeVueI18nAtInTranslationsTree(
      bundle?.translations && typeof bundle.translations === 'object'
        ? JSON.parse(JSON.stringify(bundle.translations))
        : {}
    );

    if (bundle?.property && typeof bundle.property === 'object') {
      publishedPropertyContentByLocale[loc] = {
        property: JSON.parse(JSON.stringify(bundle.property)),
        floors: Array.isArray(bundle.floors)
          ? JSON.parse(JSON.stringify(bundle.floors))
          : []
      };
    } else {
      publishedPropertyContentByLocale[loc] = { property: null, floors: [] };
    }

    const tfp = bundle?.version?.translationsFingerprint;
    publishedTranslationFingerprints[loc] =
      typeof tfp === 'string' && tfp.length > 0 ? tfp : '';
    const pfp = bundle?.version?.propertyFingerprint;
    publishedPropertyFingerprints[loc] =
      typeof pfp === 'string' && pfp.length > 0 ? pfp : '';
  }

  const defaultFloors = publishedPropertyContentByLocale[DEFAULT_LOCALE]?.floors;
  if (Array.isArray(defaultFloors) && defaultFloors.length > 0) {
    for (const loc of SUPPORTED_LOCALES) {
      if (loc === DEFAULT_LOCALE) continue;
      const entry = publishedPropertyContentByLocale[loc];
      if (!entry || !Array.isArray(entry.floors)) continue;
      entry.floors = mergeFloorsWithRoomLocaleFallback(entry.floors, defaultFloors);
    }
  }

  return {
    publishedLocaleMessages,
    publishedPropertyContentByLocale,
    publishedTranslationFingerprints,
    publishedPropertyFingerprints
  };
}

export const {
  publishedLocaleMessages,
  publishedPropertyContentByLocale,
  publishedTranslationFingerprints,
  publishedPropertyFingerprints
} = load();
